import { useState, useEffect, Dispatch, SetStateAction, memo, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { IKnmLegalBasisParams } from 'APIPackage/KnmModule/interfaces/IKnmLegalBasisParams';
import { ILegalBasisDocumentExtended } from 'APIPackage/CatalogsModule/interfaces/ILegalBasisDocumentExtended';
import { SelectField } from 'UIPackage/MainModule/components/SelectField';
import { TransferElements } from 'UIPackage/MainModule/components/TransferElements';
import { not } from 'CorePackage/MainModule/common/arrayHelpers';
import { Button } from 'UIPackage/MainModule/components/Button';

import { SelectLegalBasesItem } from './SelectLegalBasesItem';
import { LegalBasisEditingAttributesNames } from '../types/LegalBasisEditingAttributesNames';
import styles from './SelectLegalBases.module.scss';

interface IAvailableLegalBasisDocument extends ILegalBasisDocumentExtended {
  active?: boolean;
}

interface ISelectLegalBasesProps {
  legalBasesDocuments: ILegalBasisDocumentExtended[];
  selectedLegalBases: IKnmLegalBasisParams[];
  setSelectedLegalBases: Dispatch<SetStateAction<IKnmLegalBasisParams[]>>;
  onLoadButtonClick?: () => void;
}

export const SelectLegalBases = memo(
  ({ legalBasesDocuments, selectedLegalBases, setSelectedLegalBases, onLoadButtonClick }: ISelectLegalBasesProps) => {
    const [availableLegalBasesDocuments, setAvailableLegalBasesDocuments] = useState<IAvailableLegalBasisDocument[]>(
      legalBasesDocuments,
    );
    const [activeSelectedLegalBases, setActiveSelectedLegalBases] = useState<IKnmLegalBasisParams[]>([]);

    useEffect(() => {
      setAvailableLegalBasesDocuments(legalBasesDocuments);
    }, [legalBasesDocuments]);

    const handleSelectingElementToggle = useCallback(
      (id: string) => () =>
        setAvailableLegalBasesDocuments(prev =>
          prev.map(availableLegalBasis => {
            if (availableLegalBasis.id === id) {
              return {
                ...availableLegalBasis,
                active: !availableLegalBasis.active,
              };
            }

            return availableLegalBasis;
          }),
        ),
      [],
    );

    const handleSelectedElementToggle = useCallback(
      (legalBasis: IKnmLegalBasisParams) => () => {
        setActiveSelectedLegalBases(prev => {
          const isSelectedLegalBasisActive = prev.some(
            activeSelectedLegalBasis => activeSelectedLegalBasis.id === legalBasis.id,
          );

          return isSelectedLegalBasisActive
            ? prev.filter(activeSelectedLegalBasis => activeSelectedLegalBasis.id !== legalBasis.id)
            : [...prev, legalBasis];
        });
      },
      [],
    );

    const changeLegalBasisAttributeValue = useCallback(
      (id: string) => (attributeName: LegalBasisEditingAttributesNames, value: string) => {
        setSelectedLegalBases(prev => {
          return prev.map(legalBasis => {
            if (legalBasis.id === id) {
              return {
                ...legalBasis,
                [attributeName]: value,
              };
            }

            return legalBasis;
          });
        });
      },
      [setSelectedLegalBases],
    );

    const manyForward = useCallback(() => {
      setSelectedLegalBases(prev => [
        ...prev,
        ...availableLegalBasesDocuments.map(availableLegalBasis => ({
          ...availableLegalBasis,
          id: uuidv4(),
        })),
      ]);
      setAvailableLegalBasesDocuments(prev =>
        prev.map(availableLegalBasis => ({
          ...availableLegalBasis,
          active: false,
        })),
      );
    }, [availableLegalBasesDocuments, setSelectedLegalBases]);

    const oneForward = useCallback(() => {
      setSelectedLegalBases(prev => [
        ...prev,
        ...availableLegalBasesDocuments
          .filter(availableLegalBasis => availableLegalBasis.active)
          .map(availableActiveLegalBasis => ({
            ...availableActiveLegalBasis,
            id: uuidv4(),
          })),
      ]);
      setAvailableLegalBasesDocuments(prev =>
        prev.map(availableLegalBasis => ({
          ...availableLegalBasis,
          active: false,
        })),
      );
    }, [availableLegalBasesDocuments, setSelectedLegalBases]);

    const oneBackward = useCallback(() => {
      if (activeSelectedLegalBases.length) {
        setSelectedLegalBases(prev => not(prev, activeSelectedLegalBases));
        setActiveSelectedLegalBases([]);
      }
    }, [activeSelectedLegalBases, setSelectedLegalBases]);

    const manyBackward = useCallback(() => {
      setSelectedLegalBases([]);
      setActiveSelectedLegalBases([]);
    }, [setSelectedLegalBases]);

    const selectingTitleContent = useMemo(
      () => <div className={styles.SelectFieldHeaderTitle}>Название документа</div>,
      [],
    );

    const selectingElementsContent = useMemo(
      () => (
        <>
          {availableLegalBasesDocuments.map(availableLegalBasis => (
            <SelectLegalBasesItem
              key={availableLegalBasis.id}
              element={availableLegalBasis}
              active={Boolean(availableLegalBasis.active)}
              onClick={handleSelectingElementToggle(availableLegalBasis.id || '')}
            />
          ))}
          {onLoadButtonClick && (
            <Button type="button" text="Показать еще" color="loadLink" onClick={onLoadButtonClick} />
          )}
        </>
      ),
      [availableLegalBasesDocuments, handleSelectingElementToggle, onLoadButtonClick],
    );

    const selectedTitleContent = useMemo(
      () => (
        <div className={styles.SelectFieldHeader}>
          <div className={styles.SelectFieldHeaderTitle}>Название документа</div>
          <div className={styles.SelectFieldHeaderItemSection}>Раздел</div>
          <div className={styles.SelectFieldHeaderItemChapter}>Глава</div>
          <div className={styles.SelectFieldHeaderItemArticle}>Статья</div>
          <div className={styles.SelectFieldHeaderItemItem}>Пункт</div>
          <div className={styles.SelectFieldHeaderItemPart}>Часть</div>
        </div>
      ),
      [],
    );

    const selectedElementsContent = useMemo(
      () =>
        selectedLegalBases.map(selectedLegalBasis => (
          <SelectLegalBasesItem
            key={selectedLegalBasis.id}
            element={selectedLegalBasis}
            active={activeSelectedLegalBases.indexOf(selectedLegalBasis) !== -1}
            isDetailed
            onClick={handleSelectedElementToggle(selectedLegalBasis)}
            onAttributeChange={changeLegalBasisAttributeValue(selectedLegalBasis.id || '')}
          />
        )),
      [activeSelectedLegalBases, changeLegalBasisAttributeValue, handleSelectedElementToggle, selectedLegalBases],
    );

    return (
      <div className={styles.Container}>
        <div className={styles.SelectFieldBasic}>
          <h3 className={styles.SelectFieldTitle}>Доступные нормативно-правовые акты</h3>
          <SelectField titleContent={selectingTitleContent} elementsContent={selectingElementsContent} />
        </div>
        <div className={styles.TransferElements}>
          <TransferElements
            manyForward={manyForward}
            oneForward={oneForward}
            oneBackward={oneBackward}
            manyBackward={manyBackward}
          />
        </div>
        <div className={styles.SelectFieldSelected}>
          <h3 className={styles.SelectFieldTitle}>Выбранные нормативно-правовые акты</h3>
          <SelectField titleContent={selectedTitleContent} elementsContent={selectedElementsContent} />
        </div>
      </div>
    );
  },
);
